import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as React from 'react';
import { CoverageType } from '../../api/server/models/CoverageType';
import { DATA_KIND_PATH_LABEL, DATA_KINDS, PayerDataKind } from '../../services/payer-routing/payer-routing';
import { BaseButton } from '../base/button';
import { InputField } from '../base/form/input/input';
import { SelectField } from '../base/form/select/select';
import { getOptions } from '../base/select/select';
import { US_STATE_OPTIONS } from '../base/select/us-states';
import {
    emptyAddressDraft,
    IPayerAddressDraft,
    IPayerCreatePayload,
    IPayerCreateValues,
    PAYER_CREATE_INITIAL_VALUES,
    payerCreateSchema,
    toPayerCreatePayload,
} from './payer-create-form.model';
import * as S from './payer-create-form.styles';

export interface IPayerCreateFormProps {
    /**
     * Performs the create. Handles its own errors and resolves either way: a rejection here would
     * surface as Formik's unhandled-submit warning rather than as anything the user sees. The form
     * stays mounted — values kept — until the caller closes it.
     */
    onSubmit: (payload: IPayerCreatePayload) => Promise<void>;
    onCancel: () => void;
    /**
     * What this console needs to say before the user commits — on the provider console, that the
     * payer is saved under a "Generated Payer" name and finished by ENTER Support. A function
     * receives the current values, so the notice can show the name that will actually be saved.
     */
    notice?: React.ReactNode | ((values: IPayerCreateValues) => React.ReactNode);
    submitLabel?: string;
}

// `normalize` humanises the enum: the raw member reads COMMERCIALINSURANCECO.
const COVERAGE_OPTIONS = getOptions(CoverageType, { normalize: true });

/** The purposes after claims, in the drawer's order: each one a button away, none required. */
const OTHER_KINDS: PayerDataKind[] = DATA_KINDS.filter(kind => kind !== 'claim');

const fieldId = (kind: PayerDataKind, field: keyof IPayerAddressDraft): string => `payer-create-address-${kind}-${field}`;

/** One purpose's mailing address. The labels and placeholders are the ones Support's editor uses. */
const AddressFields: React.FC<{ kind: PayerDataKind; onRemove?: () => void }> = ({ kind, onRemove }) => {
    const label = DATA_KIND_PATH_LABEL[kind];
    const field = (name: keyof IPayerAddressDraft): string => `addresses.${kind}.${name}`;

    return (
        <S.AddressGroup data-address-kind={kind}>
            <S.AddressHead>
                <S.AddressTitle>{`Mailing address · ${label}`}</S.AddressTitle>
                {onRemove ? (
                    <BaseButton
                        type="button"
                        variant="text"
                        size="small"
                        aria-label={`Remove the mailing address for ${label}`}
                        onClick={onRemove}>
                        Remove
                    </BaseButton>
                ) : (
                    <S.Hint>Optional</S.Hint>
                )}
            </S.AddressHead>
            <S.AddressGrid>
                <S.WideCell>
                    <S.SmallLabel htmlFor={fieldId(kind, 'name')}>Attention / department</S.SmallLabel>
                    <S.FormField
                        id={fieldId(kind, 'name')}
                        name={field('name')}
                        component={InputField}
                        placeholder={kind === 'claim' ? 'Claims Department' : 'Department'}
                    />
                </S.WideCell>
                <S.WideCell>
                    <S.SmallLabel htmlFor={fieldId(kind, 'line')}>Address line</S.SmallLabel>
                    <S.FormField id={fieldId(kind, 'line')} name={field('line')} component={InputField} placeholder="PO BOX 30997" />
                </S.WideCell>
                <S.WideCell>
                    <S.SmallLabel htmlFor={fieldId(kind, 'city')}>City</S.SmallLabel>
                    <S.FormField id={fieldId(kind, 'city')} name={field('city')} component={InputField} placeholder="SALT LAKE CITY" />
                </S.WideCell>
                <S.Cell>
                    <S.SmallLabel htmlFor={fieldId(kind, 'state')}>State</S.SmallLabel>
                    {/* A dropdown of US codes, as in the editor: a typed "Utha" looks filled in and reaches nobody. */}
                    <S.FormField
                        inputId={fieldId(kind, 'state')}
                        name={field('state')}
                        component={SelectField}
                        options={US_STATE_OPTIONS}
                        placeholder="Select a state…"
                    />
                </S.Cell>
                <S.Cell>
                    <S.SmallLabel htmlFor={fieldId(kind, 'postal')}>Postal code</S.SmallLabel>
                    <S.FormField id={fieldId(kind, 'postal')} name={field('postal')} component={InputField} placeholder="84130-0997" />
                </S.Cell>
            </S.AddressGrid>
        </S.AddressGroup>
    );
};

/**
 * Create a payer: a name, a coverage, and where its post goes (FE-79, FE-87).
 *
 * The body only — each console wraps it in its own dialog, because the two apps open dialogs
 * through different contexts. One implementation of the fields and their rules (NS-1).
 *
 * The claims mailing address is offered from the start and the other purposes are one button away, all optional
 * until started — see `IPayerCreateValues.addresses`.
 *
 * The submit button calls `submitForm` rather than sitting in a `<form>`. `InputField` commits its
 * value on BLUR, so an Enter-key submit would validate the name as empty while the box shows text;
 * clicking the button blurs the input first.
 */
export const PayerCreateForm: React.FC<IPayerCreateFormProps> = ({
    onSubmit,
    onCancel,
    notice,
    submitLabel = 'Create payer',
}): React.ReactElement => {
    const handleSubmit = async (
        values: IPayerCreateValues,
        helpers: FormikHelpers<IPayerCreateValues>,
    ): Promise<void> => {
        try {
            await onSubmit(toPayerCreatePayload(values));
        } finally {
            helpers.setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={PAYER_CREATE_INITIAL_VALUES}
            validationSchema={payerCreateSchema}
            onSubmit={handleSubmit}>
            {(formik: FormikProps<IPayerCreateValues>): React.ReactElement => {
                const renderedNotice = typeof notice === 'function' ? notice(formik.values) : notice;
                const added = OTHER_KINDS.filter(kind => !!formik.values.addresses[kind]);
                const available = OTHER_KINDS.filter(kind => !formik.values.addresses[kind]);

                return (
                    <S.Body data-testid="payer-create-form">
                        <S.Fields>
                            <S.Section>
                                <S.Label htmlFor="payer-create-name">Name</S.Label>
                                <S.FormField
                                    id="payer-create-name"
                                    name="name"
                                    component={InputField}
                                    placeholder="Payer name"
                                    autoFocus={true}
                                />
                            </S.Section>
                            <S.Section>
                                <S.Label htmlFor="payer-create-coverage">Coverage</S.Label>
                                <S.FormField
                                    inputId="payer-create-coverage"
                                    name="coverage"
                                    component={SelectField}
                                    options={COVERAGE_OPTIONS}
                                    placeholder="Coverage type"
                                />
                            </S.Section>
                            <AddressFields kind="claim" />
                            {added.map(kind => (
                                <AddressFields
                                    key={kind}
                                    kind={kind}
                                    // Formik deletes the key when set to undefined, so a removed purpose sends nothing.
                                    onRemove={(): void => void formik.setFieldValue(`addresses.${kind}`, undefined)}
                                />
                            ))}
                            {available.length > 0 && (
                                <S.AddRow>
                                    <S.Hint>Add a mailing address for</S.Hint>
                                    {available.map(kind => (
                                        <BaseButton
                                            key={kind}
                                            type="button"
                                            variant="text"
                                            size="small"
                                            aria-label={`Add a mailing address for ${DATA_KIND_PATH_LABEL[kind]}`}
                                            onClick={(): void =>
                                                void formik.setFieldValue(`addresses.${kind}`, emptyAddressDraft())
                                            }>
                                            {`+ ${DATA_KIND_PATH_LABEL[kind]}`}
                                        </BaseButton>
                                    ))}
                                </S.AddRow>
                            )}
                            {renderedNotice && <S.Notice>{renderedNotice}</S.Notice>}
                        </S.Fields>
                        <S.Actions>
                            <BaseButton
                                type="button"
                                variant="outlined"
                                onClick={onCancel}
                                disabled={formik.isSubmitting}>
                                Cancel
                            </BaseButton>
                            <BaseButton
                                type="button"
                                variant="primary"
                                onClick={(): void => void formik.submitForm()}
                                disabled={formik.isSubmitting}>
                                {formik.isSubmitting ? 'Creating…' : submitLabel}
                            </BaseButton>
                        </S.Actions>
                    </S.Body>
                );
            }}
        </Formik>
    );
};
